import React from 'react';
import { PAPER_TITLE, PAPER_URL } from '../constants/links';

const FAQTab: React.FC = () => {
  const faqs = [
    {
      question: "What does Survey Shield do?",
      answer: "Survey Shield reviews online survey instruments for resistance to AI/bot respondents. The hosted demo focuses on Instrument Review: upload a Qualtrics .qsf export and get back a defense score, an estimated completion-likelihood for automated agents, concrete recommendations, and a copy-paste-ready Methods statement you can drop into your manuscript."
    },
    {
      question: "What kind of file do I upload?",
      answer: "A Qualtrics survey export — a .qsf file. To get one, open your survey in Qualtrics, go to Survey → Tools → Import/Export → Export Survey. The QSF is JSON containing your blocks, questions, choices, validation, and display logic. Survey Shield parses what reviewers need; the file isn't persisted beyond the review."
    },
    {
      question: "How long does the review take?",
      answer: "Typically 30–90 seconds. Survey Shield reviews your instrument against each of the eight bot-resistance categories (Logic, Visual Reasoning, Traps, Open Ends, Mouse and Keyboard Input, Behavioral, Context Awareness, ECLAIR) in parallel, then runs a final pass that consolidates the findings into a single verdict."
    },
    {
      question: "Is it free?",
      answer: "Yes. The Instrument Review on the hosted demo is the free path — we cover the LLM cost. The live runtime (Take Survey tab) is gated behind self-hosting because each run takes 5–10 minutes and burns real LLM credit per run; we don't subsidize that on the hosted demo."
    },
    {
      question: "What does Survey Shield look for?",
      answer: "We evaluate the instrument across an eight-category bot-resistance rubric grounded in Westwood et al. (PNAS 2025) and related work on detecting automated respondents: Logic (CRT, theory-of-mind, impossible-event probes), Visual Reasoning (image-based illusions, layout tricks), Traps (attention checks and human-attestation oaths), Open Ends (knowledge-gap probes, reverse-shibboleths), Mouse and Keyboard Input (map clicks, keystroke timing), Behavioral (reCAPTCHA v3, IAT latencies, total-survey-time gating), Context Awareness (weather/zipcode/time verification), and ECLAIR (refusal probes — questions safety-tuned LLMs refuse but humans answer freely). We do NOT critique the survey's substantive research design, theoretical framing, or question wording — those remain the researcher's domain."
    },
    {
      question: "What's in the report?",
      answer: "A self-contained HTML report you can open in a new tab or download. Sections: header with metadata, score panel (defense score + bot completion likelihood), copy-to-clipboard Methods statement for your paper, a Reproducibility note recording the model and config that produced the review, survey-at-a-glance, per-category cards with the quoted survey text behind each finding, top recommendations, holistic narrative, evidence appendix indexed by question ID, and APA + BibTeX citation blocks."
    },
    {
      question: "How are findings grounded?",
      answer: "Every finding cites a verbatim excerpt from your survey along with the question ID it came from. Each excerpt is checked against the actual survey text before the report is built; anything that doesn't match a real question or choice is dropped. So what you see in the final report is something you can look up directly in your QSF."
    },
    {
      question: "Can I cite Survey Shield in my paper?",
      answer: "Yes. Each report includes a copy-paste Methods-section paragraph that records the categories reviewed, the resulting scores, and the model + parameters that produced the review. Full APA and BibTeX citation entries are included separately, each with their own copy button."
    },
    {
      question: "What's the \"Take Survey\" tab?",
      answer: "The live runtime: Survey Shield drives a real browser through your survey end-to-end, then produces the same 8-category review against what actually happened during the run (rather than against the QSF text). On the hosted demo this tab shows a self-host explainer because each run takes 5–10 minutes and burns real LLM credit. To use it locally, install with pip install 'surveyshield-py[live]' and set OPENAI_API_KEY or GOOGLE_API_KEY in your environment (or a .env in the working directory) — the form will appear automatically."
    },
    {
      question: "Why don't you accept API keys on the website?",
      answer: "Pasting credentials into a third-party web form is a phishing-shaped UX and we won't ask researchers to do it. The right path is the surveyshield-py PyPI package: pip install surveyshield-py and run reviews (or surveyshield-py[live] for live takes) from a Python script or the surveyshield CLI with your own keys."
    },
    {
      question: "Defense score vs. completion likelihood?",
      answer: "Defense score (0–100, higher is better) is a weighted blend of category scores — how well the instrument resists AI respondents overall. Completion likelihood (0–100, higher is worse) is the inverse: how easily an automated agent can complete the survey. They run in opposite directions and use opposing color bands in the report."
    },
    {
      question: "Can I use this for research?",
      answer: "Yes — this is built for academic research. The method is described in \"How Vulnerable Are Consumer Research Surveys to AI Agents? Validating an Instrument-Review Framework and Auditing Recent JCR Open Materials\" (Fernandez, Low, Bogard, & Fox, 2026; https://ssrn.com/abstract=7221478). Every report carries APA and BibTeX blocks for it, and the Contact page has the full citation."
    }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* About / Research Background Section */}
      <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">About This Project</h2>

        <div className="prose prose-gray max-w-none">
          <p className="text-gray-700 leading-relaxed mb-4">
            Survey Shield is part of a research project funded by the Morrison Center for Marketing Studies
            at the UCLA Anderson School of Management. The project examines how to test and strengthen data
            quality safeguards in online surveys.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Research Overview</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Online surveys are central to both academic research and industry applications in marketing and
            data analytics. Advances in automation and generative AI have introduced increasingly
            sophisticated threats to the integrity of survey data. Bots, survey farms, and LLM-generated
            responses now enable large-scale, hard-to-detect breaches of data authenticity.
          </p>

          <p className="text-gray-700 leading-relaxed mb-4">
            The Instrument Review feature evaluates a survey's vulnerability before it goes into the field —
            scoring how well its design resists AI respondents and producing concrete, grounded recommendations
            researchers can act on. The live runtime (self-host only) complements this with end-to-end
            execution against a real survey URL.
          </p>

          <p className="text-gray-700 leading-relaxed">
            The framework and the JCR open-materials audit behind it are written up in{' '}
            <a
              href={PAPER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 underline"
            >
              {PAPER_TITLE}
            </a>
            . For more information about this research, please visit the Contact page.
          </p>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>

        <div className="space-y-6">
          {faqs.map((faq, index) => (
            <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {faq.question}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {faq.answer}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FAQTab;
